import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import ShimmerButton from "../animations/ShimmerButton";
import { Toaster, toast } from "sonner";
import { siteSocialLinks } from "../../constants/socialLinks";

const subjects = [
  "Anxiety",
  "Depression",
  "Relationship Issues",
  "Trauma",
  "Career",
  "Other"
];

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectChange = (value) => {
    setFormData(prev => ({ ...prev, subject: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const whatsappMessage = `Hello Priya ji, I found your website and would like to book a session.

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Subject: ${formData.subject}
Message: ${formData.message}`;

    window.open(
      `https://wa.me/919079048069?text=${encodeURIComponent(whatsappMessage)}`,
      "_blank"
    );

    toast.success("Opening WhatsApp...", {
      description: "Your message is ready to send!",
    });

    // Reset form
    setFormData({
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: ""
    });
  };

  return (
    <section
      id="contact"
      className="py-20 sm:py-32 bg-warm-cream/30 relative z-20"
      data-testid="contact-section"
    >
      <Toaster position="top-center" richColors />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-warm-sage uppercase tracking-[0.2em] text-xs font-bold mb-4"
          >
            Get In Touch
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-warm-brown mb-4"
          >
            Begin Your Healing Journey
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="font-sans text-warm-brown/70 max-w-2xl mx-auto"
          >
            Reach out — the first step is always the hardest, and the most important
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h3 className="font-serif text-2xl text-warm-brown mb-6">
                Contact Information
              </h3>
              
              <div className="space-y-6">
                {/* Address */}
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-2xl bg-warm-rose/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-warm-rose" />
                  </div>
                  <div>
                    <p className="font-semibold text-warm-brown">Location</p>
                    <p className="text-warm-brown/70">4 ma 56, near MONILEK HOSPITAL & RESEARCH CENTRE, Jawahar Nagar, Sector 4, Jawahar Nagar, Jaipur, Rajasthan 302004</p>
                  </div>
                </motion.div>

                {/* Phone */}
                <motion.a
                  href="tel:+919079048069"
                  whileHover={{ x: 5 }}
                  className="flex items-start gap-4 group"
                  data-testid="contact-phone"
                >
                  <div className="w-12 h-12 rounded-2xl bg-warm-rose/20 flex items-center justify-center flex-shrink-0 group-hover:bg-warm-rose/30 transition-colors">
                    <Phone className="w-5 h-5 text-warm-rose" />
                  </div>
                  <div>
                    <p className="font-semibold text-warm-brown">Phone</p>
                    <p className="text-warm-brown/70 group-hover:text-warm-rose transition-colors">
                      +91 90790 48069
                    </p>
                  </div>
                </motion.a>

                {/* Email */}
                <motion.a
                  href="mailto:priyaahuja654@gmail.com"
                  whileHover={{ x: 5 }}
                  className="flex items-start gap-4 group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 rounded-2xl bg-warm-rose/20 flex items-center justify-center flex-shrink-0 group-hover:bg-warm-rose/30 transition-colors">
                    <Mail className="w-5 h-5 text-warm-rose" />
                  </div>
                  <div>
                    <p className="font-semibold text-warm-brown">Email</p>
                    <p className="text-warm-brown/70 group-hover:text-warm-rose transition-colors">
                      priyaahuja654@gmail.com
                    </p>
                  </div>
                </motion.a>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="font-semibold text-warm-brown mb-4">Connect With Me</h4>
              <div className="flex gap-4">
                {siteSocialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${social.name} (opens in new tab)`}
                    whileHover={{ scale: 1.1, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-12 h-12 rounded-full bg-warm-card border border-warm-rose/20 flex items-center justify-center text-warm-brown/70 hover:text-white transition-all duration-300"
                    style={{
                      "--hover-bg": social.hoverColor
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = social.hoverColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "";
                    }}
                    data-testid={`social-${social.name.toLowerCase()}`}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <form
              onSubmit={handleSubmit}
              className="bg-warm-card rounded-3xl p-6 sm:p-8 border border-warm-rose/10 shadow-lg"
              data-testid="contact-form"
            >
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-warm-brown font-medium">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="mt-2 bg-warm-bg border-warm-rose/20 focus:border-warm-rose focus:ring-warm-rose/20"
                    placeholder="Enter your name"
                    data-testid="contact-form-name"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-warm-brown font-medium">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="mt-2 bg-warm-bg border-warm-rose/20 focus:border-warm-rose focus:ring-warm-rose/20"
                    placeholder="your@email.com"
                    data-testid="contact-form-email"
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="text-warm-brown font-medium">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="mt-2 bg-warm-bg border-warm-rose/20 focus:border-warm-rose focus:ring-warm-rose/20"
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="contact-form-phone"
                  />
                </div>

                {/* Subject */}
                <div>
                  <Label htmlFor="subject" className="text-warm-brown font-medium">
                    Subject
                  </Label>
                  <Select
                    value={formData.subject}
                    onValueChange={handleSubjectChange}
                  >
                    <SelectTrigger
                      id="subject"
                      className="mt-2 bg-warm-bg border-warm-rose/20 focus:border-warm-rose focus:ring-warm-rose/20"
                      data-testid="contact-form-subject"
                    >
                      <SelectValue placeholder="Select a topic" />
                    </SelectTrigger>
                    <SelectContent className="bg-warm-card border-warm-rose/20">
                      {subjects.map((subject) => (
                        <SelectItem 
                          key={subject} 
                          value={subject}
                          className="cursor-pointer hover:bg-warm-rose/10 focus:bg-warm-rose/10"
                        >
                          {subject}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="text-warm-brown font-medium">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="mt-2 bg-warm-bg border-warm-rose/20 focus:border-warm-rose focus:ring-warm-rose/20 resize-none"
                    placeholder="Tell me a bit about what you're going through..."
                    data-testid="contact-form-message"
                  />
                </div>

                {/* Submit Button */}
                <ShimmerButton
                  type="submit"
                  className="w-full"
                  data-testid="contact-form-submit"
                >
                  Send Message
                </ShimmerButton>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
